/**
 * The prompt each core agent's model actually reads: the system prompt on
 * the Supervisor's classification node and on the Knowledge Agent's answer
 * node, in the node's Parameter Input.
 *
 * Two real shapes carry it (`parameter-input-sections.ts`): the older
 * `chat_context_setting` and the AI-Native template's `system_prompt`.
 * Whichever the node has is read and written; every other key in the
 * Parameter Input is left exactly as it was.
 */

export const PROMPT_KEYS = ['system_prompt', 'chat_context_setting'] as const;

const parse = (raw: unknown): Record<string, unknown> | null => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : null;
  } catch {
    return null;
  }
};

/** The node's system prompt, or null when it has none we can edit. */
export const readPrompt = (raw: unknown): string | null => {
  const data = parse(raw);
  if (!data) return null;
  const key = PROMPT_KEYS.find((k) => typeof data[k] === 'string');
  return key ? (data[key] as string) : null;
};

/**
 * The Parameter Input with its system prompt replaced, as the JSON text the
 * table saves; null when the node has no prompt to replace.
 */
export const withPrompt = (raw: unknown, prompt: string): string | null => {
  const data = parse(raw);
  if (!data) return null;
  const key = PROMPT_KEYS.find((k) => typeof data[k] === 'string');
  if (!key) return null;
  return JSON.stringify({ ...data, [key]: prompt }, null, 2);
};

interface PromptRow {
  command?: unknown;
  nodeName?: unknown;
}

const AI_CALL = /^(GetGeminiCompletion|CallAIModel)/;

/** The agent a node's name gives it: `Supervisor Agent …` (legacy) or a
 *  `… - Supervisor - …` segment (AI-Native, `auto-agent-grouping.ts`). */
export const agentOfName = (
  name: string
): 'supervisor' | 'knowledge' | null => {
  const segments = name.split(' - ').map((s) => s.trim().toLowerCase());
  if (/^supervisor agent/i.test(name) || segments.includes('supervisor')) {
    return 'supervisor';
  }
  if (/^knowledge agent/i.test(name) || segments.includes('knowledge')) {
    return 'knowledge';
  }
  return null;
};

/** Every model call an agent makes, in table order. */
export const agentModelNodes = <R extends PromptRow>(
  rows: R[],
  agent: 'supervisor' | 'knowledge'
): R[] =>
  rows.filter(
    (r) =>
      AI_CALL.test(String(r.command ?? '')) &&
      agentOfName(String(r.nodeName ?? '')) === agent
  );

/**
 * The node holding an agent's main model call: the Supervisor's
 * classification, or the Knowledge Agent's answer step (the one that writes
 * the reply: `GetGeminiCompletionContext`, or AI-Native's "compose message").
 */
export const findModelNode = <R extends PromptRow>(
  rows: R[],
  agent: 'supervisor' | 'knowledge'
): R | undefined => {
  const calls = agentModelNodes(rows, agent);
  if (agent === 'supervisor') return calls[0];
  return (
    calls.find(
      (r) =>
        r.command === 'GetGeminiCompletionContext' ||
        /compose/i.test(String(r.nodeName ?? ''))
    ) ?? calls[0]
  );
};

/* ---- Langfuse references ------------------------------------------------ */

const REF = /\{langfuse\.([^{}\s]+)\}/g;

/** The Langfuse prompts a node names, in order: `{langfuse.<name>}` in its
 *  prompts, and its `langfuse_prompts` list. */
export const langfuseRefs = (raw: unknown): string[] => {
  const data = parse(raw);
  if (!data) return [];
  const names: string[] = [];
  ['system_prompt', 'user_prompt', 'chat_context_setting'].forEach((key) => {
    const text = data[key];
    if (typeof text === 'string') {
      [...text.matchAll(REF)].forEach((m) => names.push(m[1]));
    }
  });
  const listed = data.langfuse_prompts;
  if (Array.isArray(listed)) {
    listed.forEach((p) => {
      const name = (p as { name?: unknown } | null)?.name;
      if (typeof name === 'string') names.push(name);
    });
  }
  return [...new Set(names)];
};

/** The Parameter Input pointing at `to` wherever it named `from`, as JSON
 *  text; null when it doesn't name `from`. */
export const withRenamedRef = (
  raw: unknown,
  from: string,
  to: string
): string | null => {
  const data = parse(raw);
  if (!data || !langfuseRefs(data).includes(from)) return null;
  const next: Record<string, unknown> = { ...data };
  ['system_prompt', 'user_prompt', 'chat_context_setting'].forEach((key) => {
    const text = next[key];
    if (typeof text === 'string') {
      next[key] = text.split(`{langfuse.${from}}`).join(`{langfuse.${to}}`);
    }
  });
  if (Array.isArray(next.langfuse_prompts)) {
    next.langfuse_prompts = next.langfuse_prompts.map((p) =>
      (p as { name?: unknown } | null)?.name === from
        ? { ...(p as object), name: to }
        : p
    );
  }
  return JSON.stringify(next, null, 2);
};
